import { useState, useMemo, useCallback, useEffect } from "react";
import {
  UserCheck,
  Loader2,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { KpiBasisNote } from "@/components/reports/KpiBasisLabel";
import { useAdvisorActivity } from "@/hooks/useInvoices";
import { SectionLoadError } from "@/components/invoicing/SectionErrorBoundary";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useSnapshotPrint } from "@/context/SnapshotPrintContext";
import { sliceForSnapshotPrint } from "@/lib/snapshotPrint";

const PAGE_SIZE = 5;

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

/** Shared copy for tooltip + empty state: custom return-date scope vs calendar YTD. */
function getAdvisorActivityRangeLabel(
  returnDateFrom?: string,
  returnDateTo?: string,
): { headline: string; detail: string } {
  const fromLabel = formatDate(returnDateFrom ?? null);
  const toLabel = formatDate(returnDateTo ?? null);
  if (returnDateFrom && returnDateTo) {
    return {
      headline: `Activity for all advisors from ${fromLabel} to ${toLabel}.`,
      detail:
        "Trip Count and Invoice Count include non-voided invoices whose return dates fall within that range. Sales and Commission include closed invoices only for the same slice.",
    };
  }
  return {
    headline: "YTD activity for all advisors in your team.",
    detail:
      "Trip Count and Invoice Count include non-voided invoices where the return date falls this year. Sales and Commission include closed invoices only.",
  };
}

interface AdvisorActivityCardProps {
  viewMode: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
}

type SortField =
  | "advisorName"
  | "tripCount"
  | "invoiceCount"
  | "totalSales"
  | "totalCommission"
  | "gemStatus"
  | "lastInvoiceDate";
type SortDir = "asc" | "desc";

function SortableHeader({
  label,
  printLabel,
  mobileLabel,
  basis,
  field,
  currentField,
  currentDir,
  onSort,
  align = "right",
  className,
}: {
  label: string;
  /** Shorter column title for PDF export */
  printLabel?: string;
  /** AR-316: shorter column title below `md`, where the full one wraps. */
  mobileLabel?: string;
  /**
   * The column's date basis, rendered as a small subline under the header. Counts and
   * money read different logs, so a single row-level label would collapse two facts into
   * one and make the row look like a bug when the two halves legitimately disagree.
   */
  basis?: string;
  field: SortField;
  currentField: SortField;
  currentDir: SortDir;
  onSort: (field: SortField) => void;
  align?: "left" | "right" | "center";
  /** AR-316: lets callers drop a column below `md`. */
  className?: string;
}) {
  const isActive = currentField === field;
  const ariaSort: "none" | "ascending" | "descending" = isActive
    ? currentDir === "asc"
      ? "ascending"
      : "descending"
    : "none";
  const sortHint = isActive
    ? currentDir === "desc"
      ? "sorted descending"
      : "sorted ascending"
    : "not sorted";
  const alignClass =
    align === "right"
      ? "text-right"
      : align === "center"
        ? "text-center"
        : "text-left";
  const btnJustify =
    align === "right"
      ? "justify-end"
      : align === "center"
        ? "justify-center"
        : "justify-start";

  return (
    <th
      aria-sort={ariaSort}
      scope="col"
      className={`px-2 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider md:px-4 print:px-1.5 print:py-1.5 print:normal-case print:tracking-normal ${alignClass} ${className ?? ""}`}
    >
      <span className="hidden print:inline print:text-[10px] print:leading-tight print:font-semibold print:text-gray-600">
        {printLabel ?? label}
      </span>
      <div className={`print:hidden flex flex-col ${align === "left" ? "items-start" : align === "center" ? "items-center" : "items-end"}`}>
        <button
          type="button"
          className={`group inline-flex min-h-11 items-center gap-0.5 md:min-h-0 ${btnJustify} rounded px-0.5 py-0.5 select-none hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
          onClick={() => onSort(field)}
          aria-label={`Sort by ${label}, ${sortHint}`}
        >
          <span className="inline-flex items-center gap-0.5">
            {mobileLabel ? (
              <>
                <span className="md:hidden">{mobileLabel}</span>
                <span className="hidden md:inline">{label}</span>
              </>
            ) : (
              label
            )}
            {isActive ? (
              currentDir === "desc" ? (
                <ChevronDown className="h-3 w-3" aria-hidden />
              ) : (
                <ChevronUp className="h-3 w-3" aria-hidden />
              )
            ) : (
              <ChevronsUpDown className="h-3 w-3 opacity-30 group-hover:opacity-50" aria-hidden />
            )}
          </span>
        </button>
        {basis && (
          <span className="text-[9px] font-normal normal-case tracking-normal text-muted-foreground/70">
            {basis}
          </span>
        )}
      </div>
    </th>
  );
}

export function AdvisorActivityCard({
  viewMode,
  returnDateFrom,
  returnDateTo,
}: AdvisorActivityCardProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isPrinting = useSnapshotPrint();

  const [sortField, setSortField] = useState<SortField>("totalSales");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(0);

  const {
    data: response,
    isLoading,
    error,
    isFetching,
  } = useAdvisorActivity({
    viewMode,
    enabled: viewMode === "all",
    returnDateFrom,
    returnDateTo,
  });

  const allAdvisors = useMemo(() => response?.data ?? [], [response]);
  // Configured server-side; falls back to the shared table if an older response omits it.
  const basisLabel = response?.basisLabel;
  // Counts and money read different logs. The count basis follows the server-side date
  // config so a flip to `statement_week` reads correctly; money is always on the week the
  // statement paid, because that is the only date its log carries.
  const countBasis =
    response?.basis?.dateBasis === "statement_week"
      ? "by statement week paid"
      : "by trip return date";
  const moneyBasis = "by statement week paid";

  const sortedData = useMemo(() => {
    const copy = [...allAdvisors];
    copy.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case "advisorName":
          cmp = a.advisorName.localeCompare(b.advisorName);
          break;
        case "tripCount":
          cmp = a.tripCount - b.tripCount;
          break;
        case "invoiceCount":
          cmp = a.invoiceCount - b.invoiceCount;
          break;
        case "totalSales":
          cmp = (parseFloat(a.totalSales) || 0) - (parseFloat(b.totalSales) || 0);
          break;
        case "totalCommission":
          cmp =
            (parseFloat(a.totalCommission) || 0) -
            (parseFloat(b.totalCommission) || 0);
          break;
        case "gemStatus":
          cmp = (a.gemStatus ?? "").localeCompare(b.gemStatus ?? "");
          break;
        case "lastInvoiceDate": {
          const da = a.lastInvoiceDate ? new Date(a.lastInvoiceDate).getTime() : 0;
          const db = b.lastInvoiceDate ? new Date(b.lastInvoiceDate).getTime() : 0;
          cmp = da - db;
          break;
        }
      }
      return sortDir === "desc" ? -cmp : cmp;
    });
    return copy;
  }, [allAdvisors, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / PAGE_SIZE));

  useEffect(() => {
    setPage((p) => Math.min(p, Math.max(0, totalPages - 1)));
  }, [totalPages, sortedData.length]);

  const paginatedData = useMemo(
    () => sliceForSnapshotPrint(sortedData, page, PAGE_SIZE, isPrinting),
    [sortedData, page, isPrinting],
  );

  const rangeLabel = useMemo(
    () => getAdvisorActivityRangeLabel(returnDateFrom, returnDateTo),
    [returnDateFrom, returnDateTo],
  );

  const handleSort = useCallback(
    (field: SortField) => {
      if (field === sortField) {
        setSortDir((d) => (d === "desc" ? "asc" : "desc"));
      } else {
        setSortField(field);
        setSortDir(field === "advisorName" || field === "gemStatus" ? "asc" : "desc");
      }
      setPage(0);
    },
    [sortField],
  );

  if (error) {
    return (
      <SectionLoadError
        sectionName="Advisor Activity Log"
        error={error}
        onRetry={() => {
          queryClient.invalidateQueries({
            queryKey: ["/api/invoices/advisor-activity"],
          });
        }}
      />
    );
  }

  return (
    <div className="rounded-xl border bg-card print:max-w-full print:overflow-visible snapshot-print-advisor-card">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <UserCheck className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Advisor Activity Log</h3>
          <InfoHint
            label="How the advisor activity log is calculated"
            className="print:hidden"
          >
            <p>
              Year-to-date activity for all advisors on your team. Trip and
              invoice counts come from the invoice log; Earned Sales and
              Commission come from the statements that paid them. Each column
              carries its own basis below the header, since the two halves of
              the row read different logs.
            </p>
            {/*
              Row-level label carries what per-column subheadings cannot: the
              reporting-excluded policy the server resolved. Kept behind the
              tooltip because the header line already reads two labels' worth.
            */}
            <KpiBasisNote card="advisorActivity" override={basisLabel} />
          </InfoHint>
          {isFetching && !isLoading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" role="table">
            <thead>
              <tr className="border-b bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left px-2 py-2.5 text-xs md:px-4 font-medium text-muted-foreground uppercase tracking-wider">
                  Agent
                </th>
                <th className="text-center px-2 py-2.5 text-xs md:px-4 font-medium text-muted-foreground uppercase tracking-wider">
                  Trip Count
                </th>
                <th className="text-center px-2 py-2.5 text-xs md:px-4 font-medium text-muted-foreground uppercase tracking-wider">
                  Invoice Count
                </th>
                <th className="text-center px-2 py-2.5 text-xs md:px-4 font-medium text-muted-foreground uppercase tracking-wider">
                  Closed Sales
                </th>
                <th className="text-center px-2 py-2.5 text-xs md:px-4 font-medium text-muted-foreground uppercase tracking-wider">
                  Gross Commission
                </th>
                <th className="text-center px-2 py-2.5 text-xs md:px-4 font-medium text-muted-foreground uppercase tracking-wider">
                  GEM Status
                </th>
                <th className="text-center px-2 py-2.5 text-xs md:px-4 font-medium text-muted-foreground uppercase tracking-wider">
                  Last Invoice Date
                </th>
              </tr>
            </thead>
            <tbody>
              {[0, 1, 2].map((i) => (
                <tr key={i} className="border-b last:border-b-0">
                  <td className="px-4 py-3">
                    <Skeleton className="h-4 w-28" />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Skeleton className="h-4 w-8 mx-auto" />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Skeleton className="h-4 w-8 mx-auto" />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Skeleton className="h-4 w-20 mx-auto" />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Skeleton className="h-4 w-20 mx-auto" />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Skeleton className="h-4 w-12 mx-auto" />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Skeleton className="h-4 w-20 mx-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : allAdvisors.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-6 min-h-[200px]">
          <UserCheck className="h-8 w-8 text-muted-foreground/20 mb-2" />
          <p className="text-sm font-medium text-muted-foreground">
            No advisor activity
          </p>
          <p className="text-xs text-muted-foreground mt-1 max-w-[280px] text-center">
            {returnDateFrom && returnDateTo
              ? `No advisor activity for return dates from ${formatDate(returnDateFrom)} to ${formatDate(returnDateTo)}.`
              : "Advisor activity from invoices with a return date this year will appear here."}
          </p>
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="overflow-x-auto print:overflow-visible print:max-w-full">
            <table
              className="w-full text-sm snapshot-print-advisor-table print:table-fixed"
              role="table"
            >
              <thead>
                <tr className="border-b bg-gray-50 dark:bg-gray-800/50 print:bg-gray-100">
                  <SortableHeader
                    label="Agent"
                    field="advisorName"
                    currentField={sortField}
                    currentDir={sortDir}
                    onSort={handleSort}
                    align="left"
                  />
                  <SortableHeader
                    label="Trip Count"
                    printLabel="Trips"
                    basis={countBasis}
                    field="tripCount"
                    currentField={sortField}
                    currentDir={sortDir}
                    onSort={handleSort}
                    align="center"
                    className="hidden md:table-cell"
                  />
                  <SortableHeader
                    label="Invoice Count"
                    printLabel="Invoices"
                    basis={countBasis}
                    field="invoiceCount"
                    currentField={sortField}
                    currentDir={sortDir}
                    onSort={handleSort}
                    align="center"
                    className="hidden md:table-cell"
                  />
                  <SortableHeader
                    label="Closed Sales"
                    printLabel="Closed Sales"
                    basis={moneyBasis}
                    field="totalSales"
                    currentField={sortField}
                    currentDir={sortDir}
                    onSort={handleSort}
                    align="center"
                    className="hidden md:table-cell"
                  />
                  <SortableHeader
                    label="Gross Commission"
                    mobileLabel="Commission"
                    printLabel="Gross Commission"
                    basis={moneyBasis}
                    field="totalCommission"
                    currentField={sortField}
                    currentDir={sortDir}
                    onSort={handleSort}
                    align="center"
                  />
                  <SortableHeader
                    label="GEM Status"
                    printLabel="GEM"
                    field="gemStatus"
                    currentField={sortField}
                    currentDir={sortDir}
                    onSort={handleSort}
                    align="center"
                    className="hidden md:table-cell"
                  />
                  <SortableHeader
                    label="Last Invoice Date"
                    printLabel="Last Invoice"
                    field="lastInvoiceDate"
                    currentField={sortField}
                    currentDir={sortDir}
                    onSort={handleSort}
                    align="center"
                    className="hidden md:table-cell"
                  />
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row) => {
                  const isCurrentUser = row.advisorId === user?.id;
                  return (
                    <tr
                      key={row.advisorId}
                      className={`border-b last:border-b-0 hover:bg-muted/20 transition-colors print:hover:bg-transparent ${
                        isCurrentUser
                          ? "bg-orange-50/50 dark:bg-orange-950/10 print:bg-orange-50/80"
                          : ""
                      }`}
                    >
                      <td className="px-2 py-2.5 text-sm font-medium md:px-4 print:px-1.5 print:py-1 print:text-[10px] print:break-words print:leading-snug">
                        {row.advisorName}
                        {isCurrentUser && (
                          <span className="ml-1 text-muted-foreground print:text-[9px]">
                            (You)
                          </span>
                        )}
                        {/* AR-316: below `md` the count columns are dropped and
                            these move here. Their basis moves with them — the
                            header that carried it is hidden, and counts sit on a
                            different date basis than the commission beside
                            them, which without the label reads as a bug. */}
                        <span className="mt-0.5 block text-xs font-normal text-muted-foreground md:hidden print:hidden">
                          {row.tripCount} trip{row.tripCount === 1 ? "" : "s"} ·{" "}
                          {row.invoiceCount} invoice{row.invoiceCount === 1 ? "" : "s"}
                        </span>
                        <span className="block text-[10px] font-normal normal-case tracking-normal text-muted-foreground/70 md:hidden print:hidden">
                          {countBasis}
                        </span>
                      </td>
                      <td className="hidden px-2 py-2.5 text-sm text-center tabular-nums md:table-cell md:px-4 print:px-1.5 print:py-1 print:text-[10px]">
                        {row.tripCount}
                      </td>
                      <td className="hidden px-2 py-2.5 text-sm text-center tabular-nums md:table-cell md:px-4 print:px-1.5 print:py-1 print:text-[10px]">
                        {row.invoiceCount}
                      </td>
                      <td className="hidden px-2 py-2.5 text-sm text-center tabular-nums md:table-cell md:px-4 print:px-1.5 print:py-1 print:text-[10px]">
                        {formatCurrency(parseFloat(row.totalSales) || 0)}
                      </td>
                      <td className="px-2 py-2.5 text-sm text-center tabular-nums md:px-4 print:px-1.5 print:py-1 print:text-[10px]">
                        {formatCurrency(parseFloat(row.totalCommission) || 0)}
                      </td>
                      <td className="hidden px-2 py-2.5 text-sm text-center md:table-cell md:px-4 print:px-1.5 print:py-1 print:text-[10px]">
                        {row.gemStatus || "—"}
                      </td>
                      <td className="hidden px-2 py-2.5 text-sm text-center tabular-nums md:table-cell md:px-4 print:px-1.5 print:py-1 print:text-[10px] print:whitespace-nowrap">
                        {formatDate(row.lastInvoiceDate)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {isPrinting && sortedData.length > 0 && (
            <p className="hidden print:block px-4 py-2 text-xs text-gray-500 border-t">
              {sortedData.length} advisor{sortedData.length === 1 ? "" : "s"} ·{" "}
              {rangeLabel.headline}
            </p>
          )}

          {/* Pagination */}
          {totalPages > 1 && !isPrinting && (
            <div className="flex items-center justify-end gap-2 px-4 py-2 border-t print:hidden">
              <Button
                variant="ghost"
                size="sm"
                className="h-11 w-11 p-0 md:h-7 md:w-7"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="text-xs text-muted-foreground tabular-nums">
                {page + 1} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-11 w-11 p-0 md:h-7 md:w-7"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
